import {
  convertReceiveQtyToStock,
  getConversionFactor,
  normalizeStockUnitLabel,
} from "./unit-conversion";

export type ParsedConversionFormula =
  | { ok: true; factor: number; explanation: string }
  | { ok: false; error: string };

/** แปลงข้อความสูตร → factor (หน่วยขายต่อ 1 หน่วยรับเข้า) */
export function parseConversionFormula(input: string): ParsedConversionFormula {
  const trimmed = input.trim();
  if (!trimmed) {
    return { ok: false, error: "ກະລຸນາໃສ່ສູດ ຫຼື ຕົວເລກ" };
  }

  const plainNumber = trimmed.replace(/,/g, "").replace(/\s/g, "");
  if (/^\d+(?:\.\d+)?$/.test(plainNumber)) {
    const factor = Number(plainNumber);
    if (factor <= 0) {
      return { ok: false, error: "ອັດຕາແປງຕ້ອງມີຄ່າຫຼາຍກວ່າ 0" };
    }
    return {
      ok: true,
      factor,
      explanation: `1 → ${formatFactor(factor)}`,
    };
  }

  const ratioMatch = trimmed.match(
    /^(\d+(?:\.\d+)?)\s*[^0-9=]*=\s*(\d+(?:\.\d+)?)/,
  );
  if (ratioMatch) {
    const left = Number(ratioMatch[1]);
    const right = Number(ratioMatch[2]);
    if (
      !Number.isFinite(left) ||
      !Number.isFinite(right) ||
      left <= 0 ||
      right <= 0
    ) {
      return { ok: false, error: "ຮູບແບບສູດບໍ່ຖືກຕ້ອງ" };
    }
    const factor = right / left;
    return {
      ok: true,
      factor,
      explanation: `${left} = ${right} → factor ${formatFactor(factor)}`,
    };
  }

  return {
    ok: false,
    error: "ໃຊ້ແບບ 100 ຫຼື 1=100 ຫຼື 1 ມັດ = 100 ຊິ້ນ",
  };
}

export function previewUnitConversion(input: {
  receiveQty: number;
  factor?: number | string | null;
  receiveUnitLabel?: string | null;
  stockUnitLabel?: string | null;
}) {
  const factor = getConversionFactor(input.factor);
  const receiveQty = Number.isFinite(input.receiveQty) ? input.receiveQty : 0;
  const stockQty = convertReceiveQtyToStock(receiveQty, factor);
  const receiveUnit =
    input.receiveUnitLabel?.trim() ||
    normalizeStockUnitLabel(input.stockUnitLabel);
  const stockUnit = normalizeStockUnitLabel(input.stockUnitLabel);

  return {
    factor,
    receiveQty,
    stockQty,
    receiveUnit,
    stockUnit,
    summary:
      receiveQty > 0
        ? `ຮັບ ${receiveQty.toLocaleString("lo-LA")} ${receiveUnit} → ${stockQty.toLocaleString("lo-LA")} ${stockUnit}`
        : `1 ${receiveUnit} = ${formatFactor(factor)} ${stockUnit}`,
  };
}

function formatFactor(value: number): string {
  if (Number.isInteger(value)) return String(value);
  return value.toLocaleString("lo-LA", { maximumFractionDigits: 4 });
}
